import {
  forwardRef,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import isArray from "lodash/isArray";
import isNil from "lodash/isNil";
import { useDispatch, useSelector } from "react-redux";

import Flx from "../../components/layout/Flx";
import Screen from "../../components/layout/Screen";
import TitledHeader from "../../components/layout/TitledHeader";

import {
  AssignmentOutlined,
  CommentOutlined,
  CreateNewFolderOutlined,
  DeleteOutline,
  FilterAltOutlined,
  FilterListRounded,
  Fullscreen,
  FullscreenExit,
  KeyboardArrowDown,
  NorthOutlined,
  RestorePageOutlined,
  RotateLeftOutlined,
  SaveOutlined,
  SettingsOutlined,
  SortRounded,
  SouthOutlined,
  UpdateOutlined,
} from "@mui/icons-material";
import { Box, Button, Divider } from "@mui/material";
import { blue, blueGrey, grey } from "@mui/material/colors";
import { isEmpty, size } from "lodash";
import useLocalStorageState from "use-local-storage-state";
import CheckboxInput from "../../_src_shared/components/inputs/CheckboxInput";
import AgGridAppliedFilters from "../../components/agGrid/AgGridAppliedFilters";
import UpdateLoanDataModal from "../../components/common/Loan/UpdateLoanDataModal";
import SearchInput from "../../components/inputs/SearchInput";
import LoadingFallback from "../../components/LoadingFallback";
import ToggleNavigation from "../../components/navigation/ToggleNavigation";
import Txt from "../../components/typography/Txt";
import DropdownButton from "../../components/ui/buttons/DropdownButton";
import TableSortDropdown from "../../components/ui/TableSortDropdown";
import { LOCAL_STORAGE_KEYS } from "../../constants/keyNames/localStorage";
import { LOAN_PIPELINE_FILTER_LABEL_ALL } from "../../constants/loanPipeline/LOAN_PIPELINE_FILTER_LABEL_ALL";
import { LOAN_STATUS_LABEL_ALL_ACTIVE } from "../../constants/loanPipeline/LOAN_STATUS_LABEL_ALL_ACTIVE";
import { selectOptionsLoanStatus } from "../../constants/selectOptions/selectOptionsLoanStatus";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { useReduxHook } from "../../hooks/useReduxHook";
import { useReduxLoanDrilldownHook } from "../../hooks/useReduxLoanDrilldownHook";
import { useUsersHook } from "../../hooks/useUsersHook";
import { loanPipelineSet } from "../../redux/actions/loanPipelineActions";
import { navSidebarToggle } from "../../redux/actions/navSidebarActions";
import { userActionSet } from "../../redux/actions/userActions";
import { filterLoansByUserEmail } from "../../utils/loanPipeline/filterLoansByUserEmail";
import CreateLoanButton from "./buttons/CreateLoanButton";
import DownloadPipelineAsZipButton from "./buttons/DownloadPipelineAsZipButton";
import TableSettingsButton from "./buttons/TableSettingsButton";
import ScreenBodyWrapper from "./components/ScreenBodyWrapper";
import LoanPipelineTable from "./LoanPipelineTable/LoanPipelineTable";
import LoanPipelineTooltipDataKeyTransferModal from "./LoanPipelineTooltipDataKeyTransferModal";
import LoanPipelineSelector from "./selectors/LoanPipelineSelector";

const TABLE_KEY = "loanPipeline";

const LoanPipelineScreen = () => {
  const ref = useRef();
  const [quickFilter, setQuickFilter] = useState(null);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanPipelinePreFiltered = useSelector((state) => state?.loanPipeline);
  const { userEmail } = useReduxHook();

  const savedColumnState = useSelector(
    (state) =>
      state?.user?.saved?.agGrid?.states?.[TABLE_KEY]?.state?.columnState,
  );

  const { loading, getUserPipeline } = usePipelineHook();
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();

  const dispatch = useDispatch();
  const isFetchingRef = useRef(false);

  // Local State
  const [fullScreen, setFullScreen] = useState(false);
  const [showTabber, setShowTabber] = useState(false);
  const [advancedFiltering, setAdvancedFiltering] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);

  // Local Storage State
  const [pipeline, setPipeline] = useLocalStorageState(
    LOCAL_STORAGE_KEYS.LOAN_PIPELINE_TABLE.DEFAULT_TABLE_PIPELINE_FILTER,
    { defaultValue: "Underwriting" },
  );
  const [loanStatusFilter, setLoanStatusFilter] = useLocalStorageState(
    LOCAL_STORAGE_KEYS.LOAN_PIPELINE_TABLE.DEFAULT_TABLE_STATUS_FILTER,
    { defaultValue: LOAN_STATUS_LABEL_ALL_ACTIVE },
  );

  const [sortFilter, setSortFilter] = useLocalStorageState(
    LOCAL_STORAGE_KEYS.LOAN_PIPELINE_TABLE.DEFAULT_TABLE_SORT,
    {
      defaultValue: {
        colId: "updateDateTime",
        sort: "desc",
      },
    },
  );
  // const [showTooltipHover, setShowTooltipHover] = useLocalStorageState(
  //   LOCAL_STORAGE_KEYS.LOAN_PIPELINE_TABLE
  //     .LOAN_PIPELINE_TABLE_TOOLTIP_HOVER_STORAGE_KEY,
  //   {
  //     defaultValue: false,
  //   },
  // );

  const [pipelineSelectorCollapsed, setPipelineSelectorCollapsed] =
    useLocalStorageState(
      LOCAL_STORAGE_KEYS.LOAN_PIPELINE_TABLE
        .DEFAULT_TABLE_PIPELINE_FILTER_COLLAPSED,
      {
        defaultValue: false,
      },
    );

  const [assignedToMe, setAssignedToMe] = useLocalStorageState(
    LOCAL_STORAGE_KEYS.LOAN_PIPELINE_TABLE.FILTER_BY_USER_SALESPERSON,
    {
      defaultValue: false,
    },
  );

  const loanPipeline = useMemo(() => {
    if (assignedToMe) {
      return filterLoansByUserEmail(loanPipelinePreFiltered, userEmail);
    }

    return loanPipelinePreFiltered;
  }, [loanPipelinePreFiltered, assignedToMe, userEmail]);
  /**
   * ##########
   * Functions
   * ##########
   */

  const onPipelineStatusChange = (newPipeline) => {
    setPipeline(newPipeline);
  };

  const fetchLoanPipeline = useCallback(() => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    getUserPipeline({
      onSuccessFn: (data) => {
        dispatch(loanPipelineSet(data));
        isFetchingRef.current = false;
      },
      onErrorFn: (err) => {
        console.error("GetUserPipeline error:", err);
        isFetchingRef.current = false;
      },
    });
  }, [getUserPipeline, dispatch]);

  useEffect(() => {
    if (isNil(loanPipeline) && !isFetchingRef.current) {
      fetchLoanPipeline();
    }
    if (!isNil(loanDrilldown)) {
      clearLoanDrilldownData();
      // dispatch(loanDrilldownClear());
      // dispatch(sidebarReset());
    }
  }, [loanPipeline, fetchLoanPipeline, loanDrilldown, dispatch]);

  const onLoanSelect = useCallback((data, field) => {
    setSelectedLoan({ loan: data, field });
  }, []);

  const onLoanSelectCancel = useCallback(() => {
    setSelectedLoan(null);
  }, []);

  const onSortFilterChange = useCallback(
    (sortState, suppressToggle) => {
      // console.log("1. sortState", sortState);
      const { colId, sort } = sortState;

      const sortValue =
        sortFilter?.colId === colId && sortFilter?.sort === sort ? null : sort;
      let finalSortState = { colId, sort: sortValue };
      if (suppressToggle) {
        finalSortState = sortState;
      }
      // console.log("2. finalSortState", finalSortState);
      if (ref.current && ref.current.api && ref.current.api.applyColumnState) {
        const columnState = [finalSortState];
        ref.current.api.applyColumnState({
          state: columnState,
          defaultState: { sort: null },
        });

        setSortFilter(finalSortState);
      }
    },
    [sortFilter],
  );

  const onFirstDataRendered = useCallback(() => {
    onSortFilterChange(sortFilter, true);

    if (savedColumnState) {
      ref?.current?.api?.applyColumnState({
        state: savedColumnState,
        applyOrder: true,
      });
    }
  }, [onSortFilterChange, sortFilter, savedColumnState]);

  const onFullScreenToggle = useCallback(
    (props) => {
      setFullScreen((prev) => !prev);
      dispatch(navSidebarToggle());
    },
    [dispatch],
  );

  const screenStyles = useMemo(() => {
    if (fullScreen) {
      return {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 11111,
        background: "#ffffff",
        ".ag-column-first.ag-cell-range-left": {
          display: "flex",
          alignItems: "center",
        },
      };
    }
    return {
      ".ag-column-first.ag-cell-range-left": {
        display: "flex",
        alignItems: "center",
      },
    };
  }, [fullScreen]);

  // const tableSettings = [
  //   {
  //     id: "tooltip-hover",
  //     type: "switch",
  //     label: "Show tooltip on hover",
  //     description: "Tooltip appears when hovering over loan rows",
  //     // description: (value) =>
  //     //   value
  //     //     ? "Tooltip appears when hovering over loan rows"
  //     //     : "Tooltip hover is disabled",
  //     value: showTooltipHover,
  //     onChange: setShowTooltipHover,
  //   },
  //   // {
  //   //   type: "divider",
  //   // },
  //   // {
  //   //   id: "auto-refresh",
  //   //   type: "switch",
  //   //   label: "Auto-refresh table",
  //   //   description: "Automatically refresh data every 30 seconds",
  //   //   value: autoRefresh,
  //   //   onChange: setAutoRefresh,
  //   // },
  //   // {
  //   //   id: "compact-view",
  //   //   type: "switch",
  //   //   label: "Compact view",
  //   //   description: (value) =>
  //   //     value ? "Showing compact row height" : "Showing standard row height",
  //   //   value: compactView,
  //   //   onChange: setCompactView,
  //   // },
  //   // Example of custom render
  //   // {
  //   //   id: "custom-setting",
  //   //   type: "custom",
  //   //   render: () => (
  //   //     <Box>
  //   //       <Typography variant="caption">Custom Setting</Typography>
  //   //       <Button>Custom Action</Button>
  //   //     </Box>
  //   //   ),
  //   // },
  // ];

  // Displayed Content
  return (
    <Screen sx={screenStyles}>
      {fullScreen ? null : (
        // <TitledHeader
        //   hideBottomBorder
        //   title={"Loan Pipeline"}
        //   titleEndContent={
        //     <Tooltip title="Refresh loan data">
        //       <IconButton
        //         size="small"
        //         onClick={() => dispatch(loanPipelineSet(null))}
        //       >
        //         <RefreshOutlined fontSize="small" />
        //       </IconButton>
        //     </Tooltip>
        //   }
        //   quickFilter={quickFilter}
        //   sx={{ pb: 1 }}
        //   setQuickFilter={setQuickFilter}
        //   endContent={
        //     <Flx gap={1} jb ac>
        //       <ManageColumnStateButton
        //         ref={ref}
        //         size="small"
        //         savedColumnState={savedColumnState}
        //       />
        //       <CreateLoanButton size="small" />
        //       <DownloadPipelineAsZipButton />
        //     </Flx>
        //   }
        // >
        //   <Box
        //     sx={{
        //       border: `1px solid ${grey[200]}`,
        //       backgroundColor: grey[50],
        //       borderRadius: 1,
        //       p: 0.4,
        //       display: "flex",
        //       flexDirection: "column",
        //       width: "max-content",
        //       gap: 0.2,
        //       ".MuiButtonBase-root": {
        //         p: "4px 6px",
        //         fontSize: "12px",
        //       },
        //     }}
        //   >
        //     <Flx ac jb>
        //       <Flx ac>
        //         <Txt sx={{ px: 1, fontStyle: "italic", fontSize:'15px' }} secondary>
        //           Active Pipeline:
        //         </Txt>
        //         <LoanPipelineSelector
        //           loans={loanPipeline}
        //           value={pipeline}
        //           onChange={onPipelineStatusChange}
        //         />
        //       </Flx>
        //       <Flx center>
        //         <Tooltip title={"Toggle Loan Status"}>
        //           <IconButton
        //             size="small"
        //             onClick={() => setPipelineSelectorCollapsed((s) => !s)}
        //           >
        //             <KeyboardArrowDown
        //               sx={{
        //                 transform: pipelineSelectorCollapsed
        //                   ? "rotate(0deg)"
        //                   : "rotate(180deg)",
        //                 transition: "transform 0.2s",
        //               }}
        //             />
        //           </IconButton>
        //         </Tooltip>
        //         <Tooltip title="Refresh loan data">
        //           <IconButton
        //             size="small"
        //             onClick={() => dispatch(loanPipelineSet(null))}
        //           >
        //             <RefreshOutlined fontSize="small" />
        //           </IconButton>
        //         </Tooltip>
        //       </Flx>
        //     </Flx>
        //     {!pipelineSelectorCollapsed && (
        //       <LoanPipelineStatusSortDropdown
        //         loans={loanPipeline}
        //         pipelineFilter={pipeline}
        //         selectedSort={loanStatusFilter}
        //         navigation
        //         onSortChange={setLoanStatusFilter}
        //       />
        //     )}
        //   </Box>
        // </TitledHeader>
        <Flx fw jb wrap g={1} sx={{ p: 1.4, alignItems: "flex-start" }}>
          <Flx g={1.5} sx={{ alignItems: "flex-start" }}>
            <Box
              sx={{
                border: `1px solid ${grey[200]}`,
                backgroundColor: grey[50],
                borderRadius: 1,

                display: "flex",
                flexDirection: "column",
                width: "max-content",
                gap: 0.2,
                "&.Mui-selected": {
                  background: blueGrey[50],
                },
                // ".MuiButtonBase-root": {
                //   p: "4px 6px",
                //   fontSize: "12px",
                // },
              }}
            >
              <Flx ac jb>
                <LoanPipelineSelector
                  loans={loanPipeline}
                  value={pipeline}
                  onChange={onPipelineStatusChange}
                />

                <Flx center sx={{ ml: 2 }}>
                  {pipelineSelectorCollapsed && (
                    <Tooltip title={"Show Loan Status Selector"}>
                      <IconButton
                        size="small"
                        onClick={() => setPipelineSelectorCollapsed(false)}
                      >
                        <KeyboardArrowDown
                          sx={{
                            transform: pipelineSelectorCollapsed
                              ? "rotate(0deg)"
                              : "rotate(180deg)",
                            transition: "transform 0.2s",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Refresh loan data">
                    <IconButton
                      size="small"
                      sx={{ mr: 0.8 }}
                      onClick={() => dispatch(loanPipelineSet(null))}
                    >
                      <RefreshOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Flx>
              </Flx>
              {!pipelineSelectorCollapsed && (
                <LoanPipelineStatusSortDropdown
                  loans={loanPipeline}
                  pipelineFilter={pipeline}
                  selectedSort={loanStatusFilter}
                  navigation
                  pipelineSelectorCollapsed={pipelineSelectorCollapsed}
                  setPipelineSelectorCollapsed={setPipelineSelectorCollapsed}
                  onSortChange={setLoanStatusFilter}
                />
              )}
            </Box>
          </Flx>
          <CreateLoanButton size="medium" />
        </Flx>
      )}

      <Flx
        fw
        jb
        ac
        sx={{
          p: 1,
          borderTop: `1px solid ${grey[300]}`,
          ".pipeline-selector .MuiButtonBase-root": {
            px: 1.6,
            fontSize: "13px",
            m: 0,
          },
          ".status-selector": {
            mt: 0.2,
            mb: 0.1,
            ml: 1,
            background: "#2962ff14",
            border: `1px solid ${blueGrey[50]}`,
            ".MuiButtonBase-root": {
              px: 1.3,
              py: 0.6,
              fontSize: "10px",
              m: 0,
            },
          },
          //  fw jb ac wrap gap={2} sx={{ px: 1 }}
        }}
      >
        <Flx gap={1.5} ac>
          <SearchInput
            value={quickFilter}
            onChange={setQuickFilter}
            placeholder={"Search in table..."}
            sx={{
              width: "225px",
              ".MuiInputBase-root": {
                borderRadius: "50px !important",
                background: "#ffffff",
                fontSize: "11px",
                pl: 1.2,
                pr: 0.5,
                ".MuiSvgIcon-root": {
                  fontSize: "11px",
                },
              },
              input: {
                py: 1,
                height: "unset",
                borderRadius: "50px",
                fontSize: "11px",
              },
            }}
          />
          <Flx ac gap={0.6} sx={{ ml: 1 }}>
            <Flx ac gap={0.4}>
              <AssignmentOutlined sx={{ fontSize: "15px", opacity: 0.5 }} />
              <Txt sx={{ fontSize: "11px" }} secondary>
                Loan Status
              </Txt>
            </Flx>
            <LoanPipelineStatusSortDropdown
              pipelineFilter={pipeline}
              loans={loanPipeline}
              selectedSort={loanStatusFilter}
              onSortChange={setLoanStatusFilter}
            />
          </Flx>
          <Flx ac gap={0.6} sx={{ ml: 1 }}>
            <Flx ac gap={0.4}>
              <SortRounded sx={{ fontSize: "15px", opacity: 0.5 }} />
              <Txt sx={{ fontSize: "11px" }} secondary>
                Sort By
              </Txt>
            </Flx>
            <LoanPipelineSortDropdown
              selectedSort={sortFilter}
              onSortChange={onSortFilterChange}
            />
          </Flx>
          <Flx ac sx={{ ml: 1 }}>
            <CheckboxInput checked={assignedToMe} onChange={setAssignedToMe} />
            <Txt sx={{ fontSize: "11px" }} secondary>
              Show Loans Assigned to Me
            </Txt>
          </Flx>
        </Flx>
        <Flx ac gap={1}>
          <AgGridAppliedFilters
            ref={ref}
            advancedFiltering={advancedFiltering}
            onAdvancedFilteringClick={setAdvancedFiltering}
            onFilterRemove={(columnId, gridRef) => {
              console.log("Filter removed:", columnId);
            }}
            onAllFiltersRemove={(gridRef) => {
              console.log("All filters cleared");
            }}
          />
          <Flx ac g={1}>
            {/* <LayoutManagerButton ref={ref} /> */}

            <Tooltip
              title={fullScreen ? "Exit Full Screen" : "Enter Full Screen"}
            >
              <IconButton
                sx={{ background: fullScreen ? blue[50] : "transparent" }}
                onClick={onFullScreenToggle}
              >
                {fullScreen ? (
                  <FullscreenExit
                    className="thin-8"
                    sx={{ color: "#2962ff" }}
                  />
                ) : (
                  <Fullscreen className="thin-8" />
                )}
              </IconButton>
            </Tooltip>
            <ManageColumnStateButton
              ref={ref}
              size="small"
              savedColumnState={savedColumnState}
            />
            <DownloadPipelineAsZipButton />
            {/* <TableSettingsButton settings={tableSettings} /> */}
          </Flx>
        </Flx>
      </Flx>

      <LoanPipelineTooltipDataKeyTransferModal />
      <Suspense fallback={<LoadingFallback />}>
        <ScreenBodyWrapper>
          <LoanPipelineTable
            ref={ref}
            rowData={loanPipeline}
            loading={loading}
            quickFilterText={quickFilter}
            onLoanSelect={onLoanSelect}
            pipelineFilter={pipeline}
            // colDefs={savedColDefs}
            setShowTabber={setShowTabber}
            // showTooltipHover={showTooltipHover}
            showTabber={showTabber}
            advancedFiltering={advancedFiltering}
            loanStatusFilter={loanStatusFilter}
            onFirstDataRendered={onFirstDataRendered}
          />
          <UpdateLoanDataModal
            loan={selectedLoan?.loan}
            field={selectedLoan?.field}
            onClose={onLoanSelectCancel}
          />
        </ScreenBodyWrapper>
      </Suspense>
    </Screen>
  );
};
// const LayoutManagerButton = forwardRef(({ children }, ref) => {
//   const [show, setShow] = useState(false);
//   const [layout, setLayout] = useState("Default");
//   const onCancel = useCallback((props) => {
//     setShow(false);
//   }, []);

//   const onChange = (n) => {
//     if (n === "Creating New Layout...") {
//       setShow(true);
//     }
//     setLayout(n);
//   };

//   const handleOnCancel = useCallback((props) => {
//     setLayout("Default");
//     setShow(false);
//   }, []);
//   const options = useMemo(() => {
//     // return ["Default", "Create New Layout"];
//     return [
//       {
//         label: "Default",
//         icon: <RestartAltOutlined />,
//         value: "Default",
//       },
//       {
//         label: "Create New Layout",
//         icon: <AddCircleOutlineRounded />,
//         value: "Creating New Layout...",
//       },
//     ];
//   }, []);

//   return (
//     <Flx ac gap={0.6} sx={{ ml: 1 }}>
//       <Flx ac gap={0.4} sx={{ flexShrink: 0 }}>
//         <ViewWeekOutlined sx={{ fontSize: "15px", opacity: 0.5 }} />
//         <Txt sx={{ fontSize: "11px" }} secondary>
//           Table Layout
//         </Txt>
//       </Flx>
//       <SelectInput
//         sx={{
//           ".MuiSelect-select": {
//             fontSize: "11px",
//             px: "12px",
//             color: "#2962ff",
//             py: 0.6,
//           },
//           fieldset: {
//             borderRadius: "8px",
//             border: "1px solid #e0e0e0",
//           },
//         }}
//         value={layout}
//         onChange={onChange}
//         options={options}
//       />
//       {show && <SaveLayoutProcess onCancel={handleOnCancel} />}
//     </Flx>
//   );
// });

// const SaveLayoutProcess = ({ onCancel }) => {
//   const [step, setStep] = useState(0);
//   const [layoutData, setLayoutData] = useState(null);
//   return (
//     <>
//       {step === 0 && (
//         <BasicModal
//           maxWidth="xs"
//           show
//           onClose={onCancel}
//           title={"Name New Layout"}
//         >
//           <InputWrapper label={"Layout Name"}>
//             <TextInput
//               value={layoutData?.name}
//               onChange={(e) => setLayoutData({ ...layoutData, name: e })}
//             />
//           </InputWrapper>
//           <Flx end sx={{ mt: 2, gap: 1 }}>
//             <Button onClick={() => setStep(1)}>Next</Button>
//           </Flx>
//         </BasicModal>
//       )}
//       {step === 1 && (
//         <UnsavedChangesAlert
//           // onSubmit={handleOnSubmit}
//           icon={<MultipleStopOutlined />}
//           onCancel={onCancel}
//           // loading={loading}
//           subText={"Once you are done arranging the table, click Save Layout."}
//           text={"Create Layout: Rearrange the table to your desired layout."}
//           // subText={`${actionTypeObj?.label} (${size(selected)})`}
//           // subText="Select from the following documents."
//           saveText={`Save ${layoutData?.name} Layout`}
//         />
//       )}
//     </>
//   );
// };

const ManageColumnStateButton = forwardRef(
  ({ size = "medium", savedColumnState }, ref) => {
    const { loading, updateUserById } = useUsersHook();
    const dispatch = useDispatch();
    const user = useSelector((state) => state?.user);
    const userId = user?._id;

    const onSaveTableState = useCallback(() => {
      if (!ref?.current?.api) {
        console.error("AG Grid API not available");
        return;
      }

      const api = ref.current.api;

      // Only save column state (order and visibility)
      const columnState = api.getColumnState();

      // Get the existing saved states
      const existingSavedStates = user?.saved?.agGrid?.states || {};

      // Prepare the update payload with just column state
      const updatedStates = {
        ...existingSavedStates,
        [TABLE_KEY]: {
          version: "33.0.0",
          updatedAt: new Date().toISOString(),
          state: {
            columnState: columnState, // Only saving this
          },
        },
      };
      console.log("columnStatecolumnState", columnState);
      // return;
      updateUserById({
        userId: userId,
        data: {
          saved: {
            ...user?.saved,
            agGrid: {
              ...user?.saved?.agGrid,
              states: updatedStates,
            },
          },
        },
        onSuccessMsg: "Layout saved",
        onSuccessFn: (d) => {
          // dispatch(userActionSet(d));
          // console.log(`Column layout saved for ${TABLE_KEY}`);
        },
        onErrorFn: (err) => {
          console.error("Failed to save column layout:", err);
        },
      });
    }, [ref, user, userId, TABLE_KEY, updateUserById, dispatch]);

    const onResetTableState = useCallback(() => {
      if (!ref?.current?.api) return;

      const existingSavedStates = user?.saved?.agGrid?.states || {};
      const { [TABLE_KEY]: removed, ...remainingStates } = existingSavedStates;

      updateUserById({
        userId: userId,
        data: {
          saved: {
            ...user?.saved,
            agGrid: {
              ...user?.saved?.agGrid,
              states: remainingStates,
            },
          },
        },
        onSuccessMsg: "Saved layout removed",
        onSuccessFn: (d) => {
          ref?.current?.api?.resetColumnState();
          dispatch(userActionSet(d));
        },
      });
    }, [ref, user, userId, TABLE_KEY, updateUserById, dispatch]);

    const onRevertToSavedLayout = useCallback(
      (props) => {
        if (!ref?.current?.api) return;

        if (savedColumnState) {
          ref?.current?.api?.applyColumnState({
            state: savedColumnState,
            applyOrder: true,
          });
        }
      },
      [savedColumnState],
    );

    const showDefaultTableLayout = useCallback((props) => {
      if (!ref?.current?.api) return;

      ref?.current?.api?.resetColumnState();
    }, []);

    const options = useMemo(
      () => [
        {
          label: "Update Saved Layout",
          onClick: onSaveTableState,
          icon: <SaveOutlined className="thin" />,
        },
        {
          label: "Revert To Saved Layout",
          onClick: onRevertToSavedLayout,
          icon: <RestorePageOutlined className="thin" />,
        },
        {
          label: "Revert To Original Table Layout",
          onClick: showDefaultTableLayout,
          icon: <RotateLeftOutlined className="thin" />,
        },
        {
          label: "Remove & Delete Saved Layout",
          onClick: onResetTableState,
          icon: <DeleteOutline className="thin" />,
        },
      ],
      [
        onSaveTableState,
        onRevertToSavedLayout,
        showDefaultTableLayout,
        onResetTableState,
      ],
    );
    return (
      <Box sx={{ display: "flex", gap: 1 }}>
        {savedColumnState ? (
          <DropdownButton
            size="small"
            loading={loading}
            variant="outlined"
            endIcon={
              <SettingsOutlined
                className="thin"
                sx={{ fontSize: "15px !important" }}
              />
            }
            // variant="outlined"
            options={options}
          >
            Manage Saved Layout
          </DropdownButton>
        ) : (
          <Button
            variant="outlined"
            size={size}
            color="gray"
            onClick={onSaveTableState}
            disabled={loading}
            startIcon={<SaveOutlined className="thin" />}
          >
            {savedColumnState ? "Update Layout" : "Save Layout"}
          </Button>
        )}
      </Box>
    );
  },
);

const LoanPipelineStatusSortDropdown = ({
  pipelineFilter,
  loans,
  selectedSort,
  onSortChange,
  pipelineSelectorCollapsed,
  setPipelineSelectorCollapsed,
  navigation,
  sx = {},
}) => {
  const loanPipelineFull = useSelector((state) => state.loanPipeline);
  const loanPipeline = loans || loanPipelineFull;

  const filteredPipelineLoans = useMemo(() => {
    if (pipelineFilter === LOAN_PIPELINE_FILTER_LABEL_ALL) {
      return loanPipeline;
    }
    if (isArray(loanPipeline)) {
      return loanPipeline.filter((loan) => loan?.pipeline === pipelineFilter);
    }
    return [];
  }, [loanPipeline, pipelineFilter]);

  // --

  const getPipelineLabel = (pipeline, status) => {
    if (status === "All") {
      return `All (${size(pipeline)})`;
    }
    if (status === LOAN_STATUS_LABEL_ALL_ACTIVE) {
      const activeStatusList = [
        "Prospect",
        "Terms Accepted",
        "Processing",
        "Appraisal Ordered",
        "Appraisal In",
        "Underwriting",
        "Final Review",
        "Clear To Close",
      ];
      const count = size(
        pipeline?.filter((loan) => activeStatusList.includes(loan?.loanStatus)),
      );
      return `${status} (${count})`;
    }
    const count = size(pipeline?.filter((loan) => loan?.loanStatus === status));
    return `${status} (${count})`;
  };

  // Helper function to get the count for a status
  const getStatusWithCount = (pipeline, status) => {
    if (status === "All") {
      return size(pipeline);
    }
    if (status === LOAN_STATUS_LABEL_ALL_ACTIVE) {
      const activeStatusList = [
        "Prospect",
        "Terms Accepted",
        "Processing",
        "Appraisal Ordered",
        "Appraisal In",
        "Underwriting",
        "Final Review",
        "Clear To Close",
      ];
      return size(
        pipeline?.filter((loan) => activeStatusList.includes(loan?.loanStatus)),
      );
    }
    return size(pipeline?.filter((loan) => loan?.loanStatus === status));
  };

  // --

  const toggleOptions = useMemo(() => {
    // let statusList = [...toggleOelectOptionsLoanStatus];
    let statusListLabels = [
      "All",
      // LOAN_STATUS_LABEL_ALL_ACTIVE,
      ...selectOptionsLoanStatus,
    ];
    let statusLabelsWithCount = statusListLabels.map((status) => ({
      label: getPipelineLabel(filteredPipelineLoans, status),
      value: status,
      count: getStatusWithCount(filteredPipelineLoans, status),
    }));

    // Filter out empty options if navigation prop is passed
    if (navigation) {
      statusLabelsWithCount = statusLabelsWithCount.filter(
        (option) => option.count > 0,
      );
    }

    return statusLabelsWithCount;
    // return mappedOptions.sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredPipelineLoans, navigation]);

  if (navigation) {
    if (isEmpty(toggleOptions)) {
      return null;
    }
    return (
      <>
        <Divider />
        <Flx ac g={1} sx={{ p: 0.5 }}>
          <Flx ac sx={{ px: 0.2 }}>
            <FilterAltOutlined className="thin" sx={{ fontSize: "12px" }} />
            <Txt
              sx={{
                // fontStyle: "italic",
                fontSize: "11px",
                // fontWeight: 500,
              }}
              secondary
            >
              Status:
            </Txt>
          </Flx>
          <ToggleNavigation
            className="pipeline-selector"
            tabs={toggleOptions}
            sx={{
              background: blueGrey[50],
              border: `1px solid ${grey[300]}`,
              ".MuiButtonBase-root": {
                fontSize: "11px !important",
                py: 0.2,
                px: 0.5,
                "&.Mui-selected": {
                  fontWeight: "500 !important",
                  borderBottomWidth: "1.5px",
                  borderTop: "1.5px solid transparent",
                },
              },
            }}
            variant={"underline"}
            value={selectedSort}
            onChange={onSortChange}
          />
          {!pipelineSelectorCollapsed && (
            <Tooltip title={"Hide Loan Status Selector"}>
              <IconButton
                sx={{ ml: 2 }}
                size="small"
                onClick={() => setPipelineSelectorCollapsed(true)}
              >
                <KeyboardArrowDown
                  sx={{
                    transform: pipelineSelectorCollapsed
                      ? "rotate(0deg)"
                      : "rotate(180deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
        </Flx>
      </>
    );
  }
  return (
    <TableSortDropdown
      options={toggleOptions}
      value={selectedSort}
      onChange={onSortChange}
      buttonText="Loan Status"
      sx={sx}
    />
  );
};

const LoanPipelineSortDropdown = ({ selectedSort, onSortChange, sx = {} }) => {
  const options = useMemo(
    () => [
      {
        label: "Last Updated (Most Recent)",
        icon: (
          <SvgSortIndicator sortDirection="desc">
            <UpdateOutlined />
          </SvgSortIndicator>
        ),
        value: {
          colId: "updateDateTime",
          sort: "desc",
        },
      },
      {
        label: "Last Updated (Least Recent)",
        icon: (
          <SvgSortIndicator sortDirection="asc">
            <UpdateOutlined />
          </SvgSortIndicator>
        ),
        value: {
          colId: "updateDateTime",
          sort: "asc",
        },
      },
      {
        label: "Newly Added Comments",
        icon: (
          <SvgSortIndicator sortDirection="desc">
            <CommentOutlined />
          </SvgSortIndicator>
        ),
        value: {
          colId: "comments",
          sort: "desc",
        },
      },
      {
        label: "Oldest Added Comments",
        icon: (
          <SvgSortIndicator sortDirection="asc">
            <CommentOutlined />
          </SvgSortIndicator>
        ),
        value: {
          colId: "comments",
          sort: "asc",
        },
      },
      {
        label: "Created Date (Newest)",
        icon: (
          <SvgSortIndicator sortDirection="desc">
            <CreateNewFolderOutlined />
          </SvgSortIndicator>
        ),
        value: {
          colId: "createDateTime",
          sort: "desc",
        },
      },
      {
        label: "Created Date (Oldest)",
        icon: (
          <SvgSortIndicator sortDirection="asc">
            <CreateNewFolderOutlined />
          </SvgSortIndicator>
        ),
        value: {
          colId: "createDateTime",
          sort: "asc",
        },
      },
    ],
    [],
  );

  return (
    <TableSortDropdown
      options={options}
      value={selectedSort}
      onChange={onSortChange}
      buttonText="Sort By"
      sx={sx}
    />
  );
};

const SvgSortIndicator = ({
  children,
  sortDirection = null, // 'asc', 'desc', or null
  arrowSize = 12,
  arrowColor = "#666",
  spacing = -8,
  position = "right", // 'right', 'left', 'top', 'bottom'
}) => {
  const renderArrow = () => {
    if (!sortDirection) return null;
    return sortDirection === "asc" ? (
      <NorthOutlined className="thin sort-arrow" />
    ) : (
      <SouthOutlined className="thin sort-arrow" />
    );
  };

  const getContainerStyle = () => {
    const baseStyle = {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      ".MuiSvgIcon-root.sort-arrow": {
        position: "absolute",
        right: `${spacing}px`,
        fontSize: "12px",
        top: "50%",
        transform: "translateY(-50%)",
      },
    };

    switch (position) {
      case "left":
        return {
          ...baseStyle,
          flexDirection: "row-reverse",
          gap: `${spacing}px`,
        };
      case "top":
        return {
          ...baseStyle,
          flexDirection: "column-reverse",
          gap: `${spacing}px`,
        };
      case "bottom":
        return { ...baseStyle, flexDirection: "column", gap: `${spacing}px` };
      case "right":
      default:
        return { ...baseStyle, flexDirection: "row", gap: `${spacing}px` };
    }
  };

  return (
    <Box sx={getContainerStyle()}>
      {children}
      {renderArrow()}
    </Box>
  );
};
export default LoanPipelineScreen;
